<template>
  <div class="d-flex flex-column" style="min-width:95vw; max-width:95vw">

    <v-sheet class="ma-1">
      <div class="d-flex flex-row flex-wrap align-center ga-2 mb-1">
        <v-text-field
          v-model="filterStartDate"
          label="Start Date"
          type="date"
          variant="outlined"
          density="compact"
          clearable
          style="min-width: 180px; max-width: 220px;"
          aria-label="Filter by start date"
          @keydown.enter="applyDateFilter"
          @keydown.space.prevent="applyDateFilter"
        />
        <v-text-field
          v-model="filterEndDate"
          label="End Date"
          type="date"
          variant="outlined"
          density="compact"
          clearable
          style="min-width: 180px; max-width: 220px;"
          aria-label="Filter by end date"
          @keydown.enter="applyDateFilter"
          @keydown.space.prevent="applyDateFilter"
        />
        <v-autocomplete
          v-model="filterStatus"
          :items="availableStatuses"
          label="Status"
          multiple
          clearable
          chips
          closable-chips
          variant="outlined"
          density="compact"
          style="min-width: 180px; max-width: 280px;"
          aria-label="Filter by status"
        />
        <v-autocomplete
          v-model="filterCampaignName"
          :items="availableCampaignNames"
          label="Campaign Name"
          multiple
          clearable
          chips
          closable-chips
          variant="outlined"
          density="compact"
          style="min-width: 260px; max-width: 460px;"
          aria-label="Filter by campaign name"
        />
        <v-btn
          color="primary"
          :loading="loadingClicks"
          :disabled="loadingClicks || filteredItems.length === 0"
          prepend-icon="mdi-cursor-default-click"
          aria-label="Fetch unique clicks for the filtered emails"
          tabindex="0"
          @click="fetchUniqueClicks"
          @keydown.enter="fetchUniqueClicks"
          @keydown.space.prevent="fetchUniqueClicks"
        >
          Get Unique Clicks
        </v-btn>
        <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-download"
          :disabled="filteredItems.length === 0"
          aria-label="Download the filtered email report"
          tabindex="0"
          @click="downloadFile"
          @keydown.enter="downloadFile"
          @keydown.space.prevent="downloadFile"
        >
          Download
        </v-btn>
      </div>
    </v-sheet>

    <v-sheet class="ma-1">
      <v-card variant="outlined" title="Everlytic Email Report" flat>
        <template v-slot:text>
          <v-text-field
            v-model="search"
            label="Filter"
            prepend-inner-icon="mdi-filter-outline"
            variant="outlined"
            single-line
            aria-label="Filter table results"
          />
        </template>

        <div v-if="loading" class="d-flex justify-center pa-8">
          <v-progress-circular indeterminate aria-label="Loading data" />
        </div>
        <div v-else-if="allItems.length === 0" class="pa-4">
          No data available.
        </div>
        <div v-else class="table-scroll-wrapper">
          <v-data-table
            :items-per-page="50"
            :items-per-page-options="[10, 25, 50, -1]"
            :headers="vuetifyHeaders"
            :items="filteredItems"
            :search="search"
            item-key="id"
            fixed-header
            role="table"
            aria-label="Everlytic email report"
            class="table-fixed"
          >
            <template v-slot:headers="{ columns, isSorted, getSortIcon, toggleSort }">
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.key"
                  class="resizable-th"
                  :style="{ width: widthFor(column.key), minWidth: widthFor(column.key) }"
                >
                  <span
                    class="th-label"
                    :class="{ sortable: column.sortable }"
                    role="button"
                    :tabindex="column.sortable ? 0 : -1"
                    :aria-label="column.sortable ? `Sort by ${column.title}` : column.title"
                    @click="column.sortable && toggleSort(column)"
                    @keydown.enter="column.sortable && toggleSort(column)"
                    @keydown.space.prevent="column.sortable && toggleSort(column)"
                  >
                    {{ column.title }}
                    <v-icon
                      v-if="column.sortable && isSorted(column)"
                      :icon="getSortIcon(column)"
                      size="small"
                    />
                  </span>
                  <span
                    v-if="column.key !== 'actions'"
                    class="col-resize-handle"
                    aria-hidden="true"
                    title="Drag to resize column"
                    @pointerdown="startResize($event, column.key)"
                  />
                </th>
              </tr>
            </template>

            <template v-slot:body.prepend>
              <tr class="grand-totals-row">
                <td
                  v-for="(col, idx) in vuetifyHeaders"
                  :key="col.key"
                  class="font-weight-bold"
                >
                  <template v-if="idx === 0">Grand Totals</template>
                  <template v-else-if="col.key === 'ctor'">
                    {{ formatPct(columnTotals.totalUniqueClicks, columnTotals.totalOpens) }}
                  </template>
                  <template v-else-if="col.key === 'ctr'">
                    {{ formatPct(columnTotals.totalUniqueClicks, columnTotals.totalDelivered) }}
                  </template>
                  <template v-else-if="col.key in columnTotals">
                    {{ columnTotals[col.key].toLocaleString() }}
                  </template>
                </td>
              </tr>
            </template>

            <template v-slot:item.ctor="{ item }">
              {{ formatPct(item.totalUniqueClicks, item.totalOpens) }}
            </template>

            <template v-slot:item.ctr="{ item }">
              {{ formatPct(item.totalUniqueClicks, item.totalDelivered) }}
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn
                :disabled="!item.public_Report_Url"
                color="primary"
                size="small"
                variant="tonal"
                prepend-icon="mdi-open-in-new"
                :href="item.public_Report_Url || undefined"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="`Open public report for ${item.subject || item.id}`"
                tabindex="0"
              >
                Report
              </v-btn>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </v-sheet>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useReportsStore } from '@/stores/reports';
import { useExcelStore } from '@/stores/excel';

const reportsStore = useReportsStore();
const excelStore = useExcelStore();

// ── Column resizing ──────────────────────────────────────────────────────────
// Vuetify's data table has no built-in column resizing, so we render the header
// row ourselves (headers slot) with a drag grip per column. Widths are kept in a
// reactive map and applied as inline th widths; table-layout:fixed makes the body
// cells follow the header width.
const columnWidths = ref({});

const widthFor = (key) =>
  columnWidths.value[key] ? `${columnWidths.value[key]}px` : undefined;

const startResize = (e, key) => {
  e.preventDefault();
  e.stopPropagation(); // don't trigger column sort

  const th = e.target.closest('th');
  const startX = e.clientX;
  const startWidth = th.offsetWidth;
  const handle = e.target;

  handle.setPointerCapture(e.pointerId);
  document.body.style.userSelect = 'none';

  const onMove = (ev) => {
    columnWidths.value[key] = Math.max(60, startWidth + (ev.clientX - startX));
  };

  const onUp = () => {
    handle.releasePointerCapture(e.pointerId);
    handle.removeEventListener('pointermove', onMove);
    handle.removeEventListener('pointerup', onUp);
    document.body.style.userSelect = '';
  };

  handle.addEventListener('pointermove', onMove);
  handle.addEventListener('pointerup', onUp);
};

const allItems     = ref([]);
const vuetifyHeaders = ref([]);
const search       = ref('');
const loading      = ref(false);
const loadingClicks = ref(false);

const filterStartDate    = ref('');
const filterEndDate      = ref('');
const filterStatus       = ref([]);
const filterCampaignName = ref([]);

// Correct camelCase keys as serialised by ASP.NET from the C# DataItem properties
const headerOrder = [
  'campaign_Name',
  'sendDate',
  'status',
  'total_Sent',
  'totalDelivered',
  'total_Bounces',
  'totalOpens',
  'totalUniqueClicks',
  'ctor',
  'ctr',
];

const headerNameMap = {
  campaign_Name:       'Campaign Name',
  sendDate:            'Sent Date',
  status:              'Status',
  total_Sent:          'Total Sent',
  totalDelivered:      'Total Delivered',
  total_Bounces:       'Total Bounced',
  totalOpens:          'Total Opened',
  totalUniqueClicks:   'Unique Clicks',
  ctor:                'CTOR',
  ctr:                 'CTR',
};

// ── Filter option lists derived from full data set ───────────────────────────

const availableStatuses = computed(() => {
  const vals = allItems.value.map(i => i.status).filter(Boolean);
  return [...new Set(vals)].sort();
});

const availableCampaignNames = computed(() => {
  const vals = allItems.value.map(i => i.campaign_Name).filter(Boolean);
  return [...new Set(vals)].sort();
});

// ── Filtered rows ────────────────────────────────────────────────────────────

const filteredItems = computed(() => {
  return allItems.value.filter(item => {
    // Date range — skip epoch dates (unsent / composing emails)
    if (filterStartDate.value || filterEndDate.value) {
      const d = item.sendDate ? new Date(item.sendDate) : null;
      if (!d || d.getFullYear() <= 1970) return false;
      if (filterStartDate.value && d < new Date(filterStartDate.value)) return false;
      if (filterEndDate.value) {
        const end = new Date(filterEndDate.value);
        end.setHours(23, 59, 59, 999);
        if (d > end) return false;
      }
    }

    if (filterStatus.value.length && !filterStatus.value.includes(item.status)) return false;
    if (filterCampaignName.value.length && !filterCampaignName.value.includes(item.campaign_Name)) return false;

    return true;
  });
});

// Sum numeric columns across the filtered rows for the grand-totals row
const columnTotals = computed(() => {
  const totals = {};
  for (const item of filteredItems.value) {
    for (const key in item) {
      // ctor/ctr are ratios — summing is meaningless; derived below from totals
      if (key === 'ctor' || key === 'ctr') continue;
      const val = item[key];
      if (typeof val === 'number') {
        totals[key] = (totals[key] || 0) + val;
      }
    }
  }
  return totals;
});

// Format a ratio (0..1) as a percentage string for display
const formatPct = (num, den) => (den > 0 ? `${((num / den) * 100).toFixed(1)}%` : '—');

// Noop — date fields are reactive; method exists for keyboard handler
const applyDateFilter = () => {};

// Export currently filtered rows. Uses visible table columns only —
// public_Report_Url / Report action column deliberately omitted.
const downloadFile = () => {
  if (filteredItems.value.length === 0) return;

  const exportRows = filteredItems.value.map(item => {
    const row = {};
    for (const key of headerOrder) {
      const title = headerNameMap[key] || key;
      if (key === 'ctor') {
        row[title] = formatPct(item.totalUniqueClicks, item.totalOpens);
      } else if (key === 'ctr') {
        row[title] = formatPct(item.totalUniqueClicks, item.totalDelivered);
      } else if (key === 'sendDate' && item.sendDate) {
        const d = new Date(item.sendDate);
        row[title] = d.getFullYear() <= 1970 ? '' : d.toISOString().slice(0, 10);
      } else {
        row[title] = item[key] ?? '';
      }
    }
    return row;
  });

  const now = new Date();
  const datestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  const fileName = `EverlyticEmailReport_${datestamp}`;

  excelStore.generateInMemoryExcelDocumentV2(exportRows, fileName).catch(error => {
    console.log('Generate In Memory Excel Document - EverlyticEmailReport', error);
  });
};

// Fetch unique-click detail for the currently filtered emails and merge the
// results back onto the rows (recomputing CTOR/CTR). Kept out of the initial
// load so the list renders without a per-email API call per row.
const fetchUniqueClicks = async () => {
  const ids = [...new Set(filteredItems.value.map(i => i.id).filter(Boolean))];
  if (ids.length === 0) return;

  loadingClicks.value = true;
  try {
    const res = await reportsStore.getEverlyticUniqueClicks(ids);
    const results = res?.items || [];
    const byId = new Map(results.map(r => [r.id, r]));

    for (const item of allItems.value) {
      const r = byId.get(item.id);
      if (!r) continue;
      item.totalClicks = r.totalClicks;
      item.totalUniqueClicks = r.totalUniqueClicks;
      item.ctor = item.totalOpens > 0 ? item.totalUniqueClicks / item.totalOpens : 0;
      item.ctr = item.totalDelivered > 0 ? item.totalUniqueClicks / item.totalDelivered : 0;
    }
  } finally {
    loadingClicks.value = false;
  }
};

// ── Data fetch ───────────────────────────────────────────────────────────────

const fetchData = async () => {
  loading.value = true;
  try {
    const res = await reportsStore.getEverlyticEmailReportData();
    const collection = res?.responseData?.collection || [];
    if (Array.isArray(collection) && collection.length > 0) {
      allItems.value = collection.map(item => {
        const d = item.data;
        // Calculated ratios (stored 0..1, formatted as % in the table)
        d.ctor = d.totalOpens > 0 ? d.totalUniqueClicks / d.totalOpens : 0;
        d.ctr = d.totalDelivered > 0 ? d.totalUniqueClicks / d.totalDelivered : 0;
        return d;
      });

      const firstItem = allItems.value[0];
      const keys = headerOrder.filter(key =>
        Object.prototype.hasOwnProperty.call(firstItem, key)
      );

      vuetifyHeaders.value = keys.map(key => ({
        key,
        title: headerNameMap[key] || key,
        sortable: true,
      }));

      vuetifyHeaders.value.push({
        key: 'actions',
        title: 'Report',
        sortable: false,
      });
    } else {
      allItems.value   = [];
      vuetifyHeaders.value = [];
    }
  } catch {
    allItems.value   = [];
    vuetifyHeaders.value = [];
  } finally {
    loading.value = false;
  }
};

onMounted(fetchData);
</script>

<style scoped>
.table-scroll-wrapper {
  overflow-x: auto;
  width: 100%;
}

.table-fixed :deep(table) {
  table-layout: fixed;
  min-width: 1200px;
}

.table-fixed :deep(th),
.table-fixed :deep(td) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resizable-th {
  position: relative;
}

.th-label {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.th-label.sortable {
  cursor: pointer;
}

.col-resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 8px;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  z-index: 2;
}

.col-resize-handle:hover {
  background-color: rgba(var(--v-border-color), 0.4);
}

.grand-totals-row {
  background-color: rgb(var(--v-theme-surface-light, 238, 238, 238));
}

.grand-totals-row td {
  border-bottom: 2px solid rgba(var(--v-border-color), 0.38);
}
</style>
